// src/components/Common/AuthorLink.jsx
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import HomeIcon from "@mui/icons-material/Home";

/**
 * AuthorLink
 * - 닉네임을 파란 링크 + 팝오버(작성글 보기)로 표시
 * - disabled == true 이면 평문 텍스트만 반환
 * - authorId가 null/undefined 이면(탈퇴자 등) 자동으로 비활성화 처리
 * - sx prop 으로 fontWeight 등 외부 스타일 덮어쓰기가 가능
 *
 * ✅ 권장 사용
 * <AuthorLink nickname={nickname} authorId={author_id} disabled={isAnonymous} />
 */
export default function AuthorLink({
    nickname,
    username,          // (미사용이어도 유지: 기존 호출부 호환)
    authorId,          // ✅ 추가: null이면 클릭/이동 금지
    disabled,
    sx: sxProp = {},
}) {
    const [anchorEl, setAnchorEl] = useState(null);

    const safeNickname = useMemo(() => (nickname ?? "").trim(), [nickname]);

    // ✅ 최종 비활성화 조건:
    // 1) 익명 등 외부에서 disabled=true
    // 2) authorId가 없으면(탈퇴자/프로필 없음) 무조건 비활성화
    const isDisabled = Boolean(disabled) || !authorId;

    /* 비활성화면 링크 없이 텍스트만 */
    if (isDisabled) return <span>{safeNickname}</span>;

    /* 클릭 → 팝오버 열기 */
    const handleOpen = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    /* 팝오버 닫기 */
    const handleClose = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        setAnchorEl(null);
    };

    return (
        <>
            {/* 닉네임 링크 */}
            <Typography
                component="span"
                variant="inherit"
                onClick={handleOpen}
                sx={{
                    fontWeight: 400,
                    color: "primary.main",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                    ...sxProp,
                }}
            >
                {safeNickname}
            </Typography>

            {/* 팝오버 메뉴 */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                MenuListProps={{ dense: true, onClick: handleClose }}
            >
                {/* 작성글 보기 */}
                <MenuItem
                    component={RouterLink}
                    to={`/main/board?page=1&author_id=${authorId}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ListItemIcon>
                        <ArticleIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="작성글 보기" />
                </MenuItem>

                {/* 미니홈 가기 */}
                {safeNickname && (
                    <MenuItem
                        component={RouterLink}
                        to={`/u/${encodeURIComponent(safeNickname)}/home`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ListItemIcon>
                            <HomeIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="미니홈 가기" />
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}
